import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

import styles from "./NotificationContext.module.css";
import IconInfo from "@/components/icons/IconInfo";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import IconError from "@/components/icons/IconError";
import Button from "@/components/forms/Button/Button";
import IconClose from "@/components/icons/IconClose";


type NotificationContextProps = {
  showNotification: (content: React.ReactNode, type?: NotificationType, heading?: React.ReactNode) => void;
};

export enum NotificationType {
  INFO,
  ERROR,
  SUCCESS
}

const NotificationContext = createContext<NotificationContextProps>(undefined);
export const useNotificationContext = () => useContext(NotificationContext);

const baseZIndex = 10000;

type Notification = {
  content: React.ReactNode; // the actual 'body' of the modal, dialog, popover, tooltip, notification, etc.
  type: NotificationType;
  timeoutId?: number;
}

export const NotificationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notificationRefs = useRef<HTMLDivElement[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [location, navigate] = useLocation();

  useEffect(() => {
    setNotifications([]); // close all notifications when the route changes
  }, [location]);

  const showNotification = (content: React.ReactNode, type: NotificationType = NotificationType.INFO) => {
    const notification: Notification = { content, type, };
    notification.timeoutId = window.setTimeout(() => {
      const notificationIndex = notifications.findIndex(not => not === notification);
      if (notificationIndex !== -1) {
        notifications.splice(notificationIndex, 1);
        setNotifications([...notifications]);
      }
    }, 5000); // 5s default timeout
    notifications.push(notification);
    setNotifications([...notifications]);
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
      }}
    >
      <div ref={anchorRef} style={{position: "absolute", left: "0", top: "0"}}>
        {notifications.map((p, i) => (
          <div
            ref={el => notificationRefs.current[i] = el}
            key={i}
            className={styles.popover}
            style={{ zIndex: baseZIndex + i }}
            role={"alert"}
          >
            <div className={styles.notification}>
              {p.type === NotificationType.INFO && (
                <span style={{color: "var(--sentiment-text-info)",display:"grid",placeItems:"center"}}>
                  <IconInfo/>
                </span>
              )}
              {p.type === NotificationType.SUCCESS && (
                <span style={{color: "var(--sentiment-text-positive)",display:"grid",placeItems:"center"}}>
                  <IconCheckCircle />
                </span>
              )}
              {p.type === NotificationType.ERROR && (
                <span style={{color: "var(--sentiment-text-negative)",display:"grid",placeItems:"center"}}>
                  <IconError/>
                </span>
              )}

              {p.content}

              <Button
                className={styles.notificationCloseIcon}
                type="text"
                onClick={() => {
                  notifications.splice(i, 1);
                  setNotifications([...notifications]);
                }}
              >
                <IconClose/>
              </Button>
            </div>
          </div>
        ))}
      </div>
      {children}
    </NotificationContext.Provider>
  );
};
